import {
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, Activity } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AddDepartment from 'components/shared/AddDepartment';
import { addDepartments } from 'queries/departmentQueries';
import { sendCustomEmail } from 'queries/emailQueries';
import { redoExecution } from 'queries/finalReveiwQueries';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import Button from '../../components/atoms/button';
import FileUpload from '../../components/atoms/file-upload';
import InputTextAreaField from '../../components/atoms/input-field/inputTextAreaField';
import InputTextField from '../../components/atoms/input-field/inputTextField';
import Toast, {
  showToasterFailure,
  showToasterSuccess,
} from '../../components/atoms/toast';
import CreatorFiles from '../../components/files/CreatorFiles';
import OwnerFiles from '../../components/files/OwnerFiles';
import { default as CustomButton } from '../../components/shared/Button';
import EstimatesSummaryTable from '../../components/tables/EstimatesSummaryTable';
import FinalReviewTable from '../../components/tables/FinalReviewTable';
import { pecrStatus } from '../../constants/pecrConstants';
import { onDownloadExecutingFileClick } from '../../helpers/downloads';
import { APIHeader, APIHeaderForUpload } from '../../index';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import {
  FinalReviewComment,
  FinalReviewGenComment,
  FinalValue,
  LocalSiteButtons,
  ReviewComment,
  ReviewDoneMessage,
  SiteReviewTitle,
  SiteReviewValue,
  TextAsLink,
  UploadedDocument,
  boldFont,
  buttonCircularProgress,
  checkboxLabel,
  commentGeneralStyle,
  commentLabel,
  costImpactHeader,
  costImpactMain,
  costImpactValueGrid,
  costImpactdisplayFiles,
  height30px,
  homeButtonComponent,
  ownerLSCInput,
  titleName,
  uploadedFilesGrid,
  validationError,
} from '../../styles';
import {
  getDownloadLink,
  getURL,
  includeNegativeForCostImpact,
  stripTrailingSlash,
  validateInputFields,
} from '../../utils';

const FinalReview = props => {
  const { apiConfig, userInfo, errorInAPI } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const urlInfo = stripTrailingSlash(window.location.toString()).split('/');
  const siteId = urlInfo.pop();
  const pecrId = urlInfo.pop();
  const url = getURL();

  const [reviewData, setReviewData] = useState([]);
  const [rawReviewData, setRawReviewData] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [estimateData, setEstimateData] = useState([]);
  const [displayFiles, setDisplayFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [inputValidationMessage, setInputValidationMessage] = useState({
    FinalComments: '',
    FinalEOComments: '',
  });
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState({});
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  const handleRowSelect = rowData => {
    setSelectedRows(prev => {
      const exists = prev.some(
        row =>
          row.DeptID === rowData.DeptID &&
          row.EIndex === rowData.EIndex &&
          row.SiteID === rowData.SiteID
      );
      if (exists) {
        return prev.filter(
          row =>
            !(
              row.DeptID === rowData.DeptID &&
              row.EIndex === rowData.EIndex &&
              row.SiteID === rowData.SiteID
            )
        );
      }
      return [...prev, rowData];
    });
  };

  const { mutateAsync } = useMutation({
    mutationFn: addDepartments,
    onSuccess: () => {
      setShowAddDepartment(false);
      setRefetchTrigger(prev => prev + 1);
    },
    onError: error => {
      console.error('Error adding departments: ', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });
  const { mutateAsync: redoExecutionMutate } = useMutation({
    mutationFn: redoExecution,
    onSuccess: () => {
      navigate('/List');
    },
    onError: error => {
      console.error('Error redoing execution: ', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  const { mutateAsync: sendCustomEmailMutation } = useMutation({
    mutationFn: sendCustomEmail,
    onError: error => {
      console.error('Error sending email:', error);
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    },
  });

  const tableHeaders = [
    'Site',
    'Department',
    'Status',
    'Lead',
    'Date',
    'Estimated_Effort',
    'Effort_Hours',
    'Estimated_Cost',
    'Costs_Currency',
    'Comments',
    'Attachements',
  ];
  const hasNotValidationError =
    !inputValidationMessage.FinalComments &&
    !inputValidationMessage.FinalEOComments;

  // Helper for error dispatch
  const notifyError = () =>
    dispatch(
      errorNotification({ hasErrorOccured: true, message: t('error_message') })
    );

  const sendEmail = body =>
    fetch(`${apiConfig.uiURL}api/sendEmail`, {
      headers: APIHeader(),
      method: 'POST',
      body: JSON.stringify(body),
    }).catch(notifyError);

  // Fetch department status and files
  useEffect(() => {
    if (!pecrId || !siteId) {
      return;
    }
    async function fetchEstimateAndFiles() {
      try {
        const res = await fetch(
          `${apiConfig.uiURL}api/FetchAllDepartmentStatus`,
          {
            method: 'POST',
            headers: APIHeader(),
            body: JSON.stringify({ pecrId, siteId }),
          }
        );
        const data = await res.json();
        if (!data.length) {
          setEstimateData([]);
          return;
        }
        fetch(`${apiConfig.uiURL}api/FetchFilesForCostImpact`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({
            MainFolder: 'PECROverview',
            SubFolder: pecrId,
            siteId,
            costImpact: 'CostImpact',
          }),
        })
          .then(res => res.json())
          .then(setDisplayFiles)
          .catch(notifyError);

        const fileResults = await Promise.all(
          data.map(async (item, idx) => {
            const payload = {
              MainFolder: 'PECROverview',
              SubFolder: pecrId,
              siteId: item.SiteID ?? item.siteId ?? item.siteID,
              departmentId:
                item.DeptID ??
                item.DeptId ??
                item.departmentId ??
                item.deptId ??
                item.deptID,
              index:
                item.EIndex ??
                item.EDIndex ??
                item.index ??
                item.eIndex ??
                item.edIndex,
              pecrId: pecrId,
            };

            if (
              payload.siteId === undefined ||
              payload.departmentId === undefined ||
              payload.index === undefined
            ) {
              console.warn(
                `Skipping department fetch at index ${idx} due to missing property:`,
                payload,
                item
              );
              return { key: null, docs: [] };
            }
            const key = `${payload.siteId}_${payload.departmentId}_${payload.index}`;
            const res = await fetch(
              `${apiConfig.uiURL}api/FetchFilesFromDepartmentExecution`,
              {
                method: 'POST',
                headers: APIHeader(),
                body: JSON.stringify(payload),
              }
            ).catch(() => null);
            const docs = res ? await res.json() : [];

            return { key, docs };
          })
        );

        const docsMap = {};
        fileResults.forEach(({ key, docs }) => {
          if (key) {
            docsMap[key] = docs;
          }
        });

        const merged = data.map(item => {
          const siteId = item.SiteID ?? item.siteId ?? item.siteID;
          const departmentId =
            item.DeptID ??
            item.DeptId ??
            item.departmentId ??
            item.deptId ??
            item.deptID;
          const index =
            item.EIndex ??
            item.EDIndex ??
            item.index ??
            item.eIndex ??
            item.edIndex;
          const key = `${siteId}_${departmentId}_${index}`;
          return { ...item, Doc: docsMap[key] || [] };
        });
        setEstimateData(merged);
        setSelectedDate(merged[0]?.TargetDate || null);
      } catch (e) {
        notifyError();
      }
    }
    fetchEstimateAndFiles();
    // eslint-disable-next-line
  }, [pecrId, siteId, apiConfig, refetchTrigger]);

  // Fetch final review data and files without merging
  useEffect(() => {
    if (!pecrId || !siteId) {
      return;
    }
    async function fetchReviewAndFiles() {
      try {
        const res = await fetch(`${apiConfig.uiURL}api/FetchPECRFinalReview`, {
          method: 'POST',
          headers: APIHeader(),
          body: JSON.stringify({ pecrId, siteId }),
        });
        const data = await res.json();

        setDisabled(
          !(data.length > 0 && (data[0].Status === 15 || data[0].Status === 0))
        );
        setRawReviewData(data);
      } catch (e) {
        notifyError();
      }
    }
    fetchReviewAndFiles();
    // eslint-disable-next-line
  }, [pecrId, siteId, apiConfig, refetchTrigger]);

  // Merge Docs from estimateData into reviewData when both are available
  useEffect(() => {
    if (
      !rawReviewData ||
      !rawReviewData.length ||
      !estimateData ||
      !estimateData.length
    ) {
      setReviewData([]);
      return;
    }

    const estimateDocMap = {};
    estimateData.forEach(est => {
      const estSiteId = est.SiteID ?? est.siteId ?? est.siteID;
      const estDeptId =
        est.DeptID ??
        est.DeptId ??
        est.departmentId ??
        est.deptId ??
        est.deptID;
      const estIndex =
        est.EIndex ?? est.EDIndex ?? est.index ?? est.eIndex ?? est.edIndex;
      const estKey = `${estSiteId}_${estDeptId}_${estIndex}`;
      estimateDocMap[estKey] = Array.isArray(est.Doc) ? est.Doc : [];
    });
    const merged = rawReviewData.map(item => {
      const siteId = item.SiteID ?? item.siteId ?? item.siteID;
      const departmentId =
        item.DeptID ??
        item.DeptId ??
        item.departmentId ??
        item.deptId ??
        item.deptID;
      const index =
        item.EIndex ??
        item.EDIndex ??
        item.index ??
        item.eIndex ??
        item.edIndex;
      const key = `${siteId}_${departmentId}_${index}`;
      const docs = estimateDocMap[key] ? [...estimateDocMap[key]] : [];

      return { ...item, Docs: docs };
    });
    merged[0].CostImpact === null &&
      (merged[0].CostImpact = estimateData[0].CostImpactOnProducts);
    setReviewData(merged);
    merged[0].TargetDate && setSelectedDate(merged[0].TargetDate);
  }, [rawReviewData, estimateData, refetchTrigger]);

  // Handlers
  const handleOnChange = (key, event) => {
    const value = event.target.value.trim();
    setReviewData(prev => [{ ...prev[0], [key]: value }]);
    setInputValidationMessage(msg => ({
      ...msg,
      [key]: validateInputFields(value, t),
    }));
  };

  const executedDeptArr = rawReviewData.filter(item => {
    return (
      item.DepartmentStatus === 'Executed' ||
      item.DepartmentStatus === 'Estimated'
    );
  });

  const onSaveClick = () => {
    setShowProgress(true);
    uploadCostImpactDocument();
    fetch(`${apiConfig.uiURL}api/InsertPECRFinalReview`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId,
        siteId,
        comments: reviewData[0].FinalComments,
        EOComments: reviewData[0].FinalEOComments,
        targetDate: selectedDate ?? null,
        costImpact: reviewData[0].CostImpact,
      }),
    })
      .then(res => {
        setShowProgress(false);
        if (res.status === 200) {
          showToasterSuccess(t('Save_message'));
        } else {
          showToasterFailure(t('Save_failed_message'));
        }
      })
      .catch(() => {
        setShowProgress(false);
        showToasterFailure(t('Save_failed_message'));
        notifyError();
      });
  };

  const onCompleteClick = () => {
    uploadCostImpactDocument(true);
    fetch(`${apiConfig.uiURL}api/InsertPECRFinalReview`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId,
        siteId,
        comments: reviewData[0].FinalComments,
        EOComments: reviewData[0].FinalEOComments,
        StatusId: pecrStatus.Submitted,
        targetDate: selectedDate ?? null,
        costImpact: reviewData[0].CostImpact,
      }),
    })
      .then(() => {
        const main = reviewData[0] || {};
        Promise.all([
          sendEmail({
            toAddress: main.OwnerEmail || '',
            fromAddress: 'PECR@carrier.com',
            loginUserEmail: userInfo.userEmail,
            Title: main.Title || '',
            PecrId: pecrId,
            type: 'assignEmail',
            toWhome: 'Final',
            Factory: main.SiteName || '',
            creatorsite: main.CreatorSiteName || '',
            creator: main.CreatorName || '',
          }),
          sendEmail({
            toAddress: main.CreatorEmail || '',
            fromAddress: 'PECR@carrier.com',
            loginUserEmail: userInfo.userEmail,
            Title: main.Title || '',
            PecrId: pecrId,
            type: 'assignEmail',
            toWhome: 'Final',
            Factory: main.SiteName || '',
            creatorsite: main.CreatorSiteName || '',
            creator: main.CreatorName || '',
          }),
          ...executedDeptArr.map(siteInfo =>
            sendEmail({
              toAddress: siteInfo.LeadEmail || '',
              fromAddress: 'PECR@carrier.com',
              loginUserEmail: userInfo.userEmail,
              Title: siteInfo.Title || '',
              PecrId: pecrId,
              type: 'assignEmail',
              toWhome: 'Final',
              Factory: siteInfo.SiteName || '',
              creatorsite: siteInfo.CreatorSiteName || '',
              creator: siteInfo.CreatorName || '',
            })
          ),
          sendEmail({
            toAddress: main.CoordinatorEmail || '',
            fromAddress: 'PECR@carrier.com',
            loginUserEmail: userInfo.userEmail,
            Title: main.Title || '',
            PecrId: pecrId,
            type: 'assignEmail',
            toWhome: 'Final',
            Factory: main.SiteName || '',
            creatorsite: main.CreatorSiteName || '',
            creator: main.CreatorName || '',
          }),
        ]).finally(() => navigate('../'));
      })
      .catch(notifyError);
  };

  const disableGCButton =
    estimateData.length > 0 && estimateData.every(item => item.Status !== null);

  const onDownloadCostImpactClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFilesForCostImpact`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId,
        costImpact: 'CostImpact',
        filename,
      }),
    })
      .then(response =>
        response.blob().then(blob => getDownloadLink(blob, filename))
      )
      .catch(notifyError);
  };

  //To upload the files in folder for CostImpact
  const uploadCostImpactDocument = (isApproveButtonClicked = false) => {
    setShowProgress(true);
    const uploadData = new FormData();
    for (const file of uploadedFiles) {
      uploadData.append('file', file);
    }
    uploadData.append('MainFolder', 'PECROverview');
    uploadData.append('SubFolder', pecrId);
    uploadData.append('siteId', siteId);
    uploadData.append('costImpact', 'CostImpact');

    // API call to upload the document
    fetch(`${apiConfig.uiURL}api/uploadDocForCostImpact`, {
      headers: APIHeaderForUpload(),
      method: 'POST',
      body: uploadData,
    })
      .then(async res => {
        if (res.status === 200) {
          //stop showing progress bar
          setShowProgress(false);
          //navigate to Home page if Approve button clicked
          isApproveButtonClicked && navigate('../');
        }
      })
      .catch(() => {
        setShowProgress(false);
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To handle the deletion of uploaded document
  const removeDoc = (event, fileName) => {
    event.preventDefault();
    const remainingFiles = displayFiles.filter(file => {
      return file !== fileName;
    });
    setDisplayFiles(remainingFiles);
    const remainingUploadFile = uploadedFiles.filter(file => {
      return file.name !== fileName;
    });
    setUploadedFiles(remainingUploadFile);
    fetch(`${apiConfig.uiURL}api/DeleteFile`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        siteId: siteId,
        fileName: fileName,
        PID: pecrId,
        folderName: 'CostImpact',
        email: userInfo.userEmail,
        phase: 'CostImpact',
      }),
    }).catch(() => {
      dispatch(
        errorNotification({
          hasErrorOccured: true,
          message: t('error_message'),
        })
      );
    });
    setErrorMessage('');
  };

  function getEmailsForReject(data) {
    const emails = [];
    data[0]?.CreatorEmail && emails.push(data[0].CreatorEmail);
    data[0]?.OwnerEmail && emails.push(data[0].OwnerEmail);
    data.forEach(item => {
      emails.push(item.LeadEmail);
    });
    return Array.from(new Set(emails));
  }
  const onRejectClick = () => {
    const est = estimateData[0] || {};
    fetch(`${apiConfig.uiURL}api/InsertPECRFinalReview`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        pecrId,
        siteId,
        ReviwerId: est.CoordinatorID || null,
        // GlobalCoordinatorID: est.GlobalCoordinatorID || null,
        StatusId: 14,
        // comments: est.OwnerLSCComments,
        comments: reviewData[0].FinalComments,
        EOComments: reviewData[0].FinalEOComments,
        targetDate: selectedDate ?? null,
        costImpact: reviewData[0].CostImpact,
      }),
    })
      .then(() => {
        const main = estimateData[0] || {};
        const emails = getEmailsForReject(estimateData);
        sendEmail({
          PecrId: pecrId,
          toAddress: emails,
          loginUserEmail: userInfo.userEmail,
          Title: main.Title || null,
          toWhome: 'Estimation SC Reject',
          Comments: reviewData?.[0]?.FinalComments ?? '',
          Description: main.Description || null,
          fromAddress: 'PECR@carrier.com',
          type: 'assignEmail',
          Factory: main.SiteName || null,
          creatorsite: main.CreatorSiteName || '',
          creator: `${main.CreatorFirstName || ''} ${
            main.CreatorLastName || ''
          }`,
        }).finally(() => navigate('../'));
      })
      .catch(notifyError);
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#1F305E',
      color: theme.palette.common.white,
      fontWeight: 'bold',
      fontSize: '17px',
    },
    [`&.${tableCellClasses.body}`]: { fontSize: '17px', whiteSpace: 'normal' },
  }));
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': { backgroundColor: theme.palette.action.hover },
  }));

  async function onSave() {
    if (Object.keys(selectedDepartments).length === 0) {
      showToasterFailure(t('Please select at least one department'));
      return;
    }
    const emails = [
      ...new Set(
        Object.values(selectedDepartments)
          .filter(item => item.Email)
          .map(item => item.Email)
      ),
    ];

    await mutateAsync({
      pecrId: pecrId,
      siteId: siteId,
      phase: 'execution',
      departments: {
        ...selectedDepartments,
      },
    });

    await sendCustomEmailMutation({
      toAddress: emails,
      fromAddress: 'PECR@carrier.com',
      subject: `You have been assigned a task to execute on PECR ${pecrId} With Title: ${
        estimateData && estimateData[0].Title
      }`,
      body: `You have been assigned as an Executor on PECR ${pecrId}. Please check the details in the Tracking File.`,
      link: `${url}TrackingFile?id=${pecrId}`,
    });
  }

  function onCancel() {
    setShowAddDepartment(false);
  }

  async function onProvideNewExecution(selectedRows) {
    const emails = Array.from(
      new Set(selectedRows.map(item => item.LeadEmail))
    );
    await redoExecutionMutate({
      pecrId: Number(pecrId),
      siteId: Number(siteId),
      departments: selectedRows,
    });

    await sendCustomEmailMutation({
      toAddress: [...emails],
      fromAddress: 'PECR@carrier.com',
      subject: `PECR ${pecrId} - New Execution Requested`,
      body: `A new execution has been requested from you on PECR ID: ${pecrId}`,
      link: `${origin}/TrackingFile?id=${pecrId}`,
    });
  }

  if (errorInAPI.hasErrorOccured) {
    return <ErrorAlert message={errorInAPI.message} />;
  }

  if (!(reviewData.length > 0 && userInfo && userInfo.userId !== '')) {
    return (
      <Grid style={homeButtonComponent}>
        <CircularProgress />
      </Grid>
    );
  }
  if (
    !(
      (
        userInfo?.active?.toString() === '1' &&
        userInfo?.userId?.toString() !== '0' &&
        reviewData.length > 0 &&
        userInfo?.userId !== null
      )
      // Number(userInfo?.userID) === Number(reviewData[0]?.CoordinatorID)
    )
  ) {
    return (
      <Grid style={homeButtonComponent}>{t('No_active_user_message')}</Grid>
    );
  }

  const main = reviewData[0] || {};
  const est = estimateData[0] || {};

  return (
    <Grid>
      {disabled && (
        <Grid style={ReviewDoneMessage}>
          {t('Review_is_already_completed')}
        </Grid>
      )}
      <Grid style={checkboxLabel} container>
        <Grid item xs={5}>
          <Grid style={SiteReviewTitle}>
            <h4 style={titleName}>
              {main.IsSingleSite === 0
                ? t('PECR_Coordinator_Review')
                : t('PECR_Global_Coordinator_Review')}
            </h4>
          </Grid>
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Title')}:</Grid>
            <Grid style={FinalValue}>
              {main.Title && pecrId ? `${main.Title}(${pecrId})` : ''}
            </Grid>
          </Grid>
          <Grid style={{ ...SiteReviewTitle, flexDirection: 'column' }}>
            <Grid style={boldFont}>{t('Description')}:</Grid>
            <Grid style={{ ...FinalValue, marginLeft: '0px' }}>
              {main.Description || ''}
            </Grid>
          </Grid>
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Product_Family')}:</Grid>
            <Grid style={SiteReviewValue} width={700}>
              {main.ProductFamily
                ? main.ProductFamily.split(',').join(', ')
                : ''}
            </Grid>
          </Grid>
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Creator')}:</Grid>
            <Grid style={SiteReviewValue}>
              {main.CreatorName && main.CreatorSiteName
                ? `${main.CreatorName} (${main.CreatorSiteName})`
                : ''}
            </Grid>
          </Grid>
          <CreatorFiles apiConfig={apiConfig} pecrId={pecrId} />
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Owner')}:</Grid>
            <Grid style={SiteReviewValue}>{main.OwnerName || ''}</Grid>
          </Grid>
          <OwnerFiles apiConfig={apiConfig} pecrId={pecrId} siteId={siteId} />
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Cost_impact_on_products')}:</Grid>
            <Grid style={SiteReviewValue}>
              {main.CostImpact && main.CurrencyUnit
                ? `${main.CostImpact} ${main.CurrencyUnit}/yr`
                : 'N/A'}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4} sx={{ marginLeft: 'auto' }}>
          <TableContainer component={Paper}>
            {est && est.TargetDate && (
              <EstimatesSummaryTable
                pecrId={pecrId}
                siteId={siteId}
                targetDate={est.TargetDate}
                currency={est.CurrencyUnit || ''}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                apiConfig={apiConfig}
                StyledTableCell={StyledTableCell}
                StyledTableRow={StyledTableRow}
                disabled={disabled || !disableGCButton}
              />
            )}
          </TableContainer>
          <Grid style={costImpactMain} width={'100%'}>
            <Grid width={'100%'} align="center" sx={costImpactHeader}>
              {t('Cost_impact_on_products')}
            </Grid>
            <Grid width={'100%'} style={costImpactValueGrid} container>
              <Grid item xs={3} style={height30px}>
                <Grid style={homeButtonComponent}>
                  <Grid sx={{ marginRight: '3px' }} width={110}>
                    <InputTextField
                      name={'CostImpactValue'}
                      placeholder={t('Cost')}
                      style={ownerLSCInput}
                      onChange={e => handleOnChange('CostImpact', e)}
                      value={main.CostImpact ?? est.CostImpactOnProducts}
                      type={'number'}
                      disabled={disabled || !disableGCButton}
                      onKeyDown={e =>
                        includeNegativeForCostImpact.includes(e.key) &&
                        e.preventDefault()
                      }
                    />
                  </Grid>
                  <Grid>{est.CurrencyUnit || ''}</Grid>
                </Grid>
              </Grid>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ backgroundColor: 'black' }}
              />
              <Grid item xs={8} style={costImpactdisplayFiles}>
                {displayFiles && displayFiles.length > 0 ? (
                  <>
                    <Grid style={UploadedDocument}>
                      {t('Uploaded_Document')}:
                    </Grid>
                    <Grid style={uploadedFilesGrid}>
                      <Grid style={UploadedDocument}>
                        {displayFiles.map((document, index) => (
                          <Tooltip
                            key={index}
                            title={document}
                            componentsProps={{
                              tooltip: { sx: { fontSize: '14px' } },
                            }}
                          >
                            <Typography
                              key={index}
                              noWrap
                              width={400}
                              style={TextAsLink}
                              onClick={() =>
                                onDownloadCostImpactClick(document)
                              }
                            >
                              {document}
                            </Typography>
                          </Tooltip>
                        ))}
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid style={UploadedDocument}>
                      {t('Uploaded_Document')}:
                    </Grid>
                    {t('No_files_have_been_uploaded')}
                  </>
                )}
                <hr
                  style={{
                    height: '2px',
                    backgroundColor: 'black',
                    width: '100%',
                  }}
                />
                <div
                  style={{
                    marginTop: '5px',
                  }}
                >
                  <FileUpload
                    displayFiles={displayFiles}
                    setDisplayFiles={setDisplayFiles}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    errorMessage={errorMessage}
                    removeDoc={removeDoc}
                    setErrorMessage={setErrorMessage}
                    disabled={disabled || !disableGCButton}
                    isOwnerLSC={true}
                    marginLeft={'0rem'}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid style={SiteReviewTitle}>
        <FinalReviewTable
          headers={tableHeaders}
          // rows={executedDeptArr}
          rows={rawReviewData}
          TextAsLink={TextAsLink}
          onDownloadExecutingFileClick={onDownloadExecutingFileClick}
          StyledTableCell={StyledTableCell}
          StyledTableRow={StyledTableRow}
          uiURL={apiConfig.uiURL}
          pecrId={pecrId}
          dispatch={dispatch}
          selectedRows={selectedRows}
          onRowSelect={handleRowSelect}
        />
      </Grid>
      <Grid style={FinalReviewComment}>
        <Grid style={ReviewComment}>
          <Grid style={commentLabel}>
            {t('Excess_and_Obsolete_parts_management')}
          </Grid>
          <InputTextAreaField
            name={'LCRComment'}
            placeholder={t('Add_Comments_here')}
            customStyle={commentGeneralStyle}
            onChange={e => handleOnChange('FinalEOComments', e)}
            value={main.FinalEOComments}
            disabled={disabled}
          />
          <Grid style={validationError}>
            {inputValidationMessage.FinalEOComments}
          </Grid>
        </Grid>
        <Grid style={ReviewComment}>
          <Grid style={FinalReviewGenComment} item xs={6}>
            <Grid style={commentLabel}>{t('General_comments')}</Grid>
            <InputTextAreaField
              name={'general_comment'}
              placeholder={t('Add_Comments_here')}
              customStyle={commentGeneralStyle}
              onChange={e => handleOnChange('FinalComments', e)}
              value={main.FinalComments}
              disabled={disabled}
            />
            <Grid style={validationError}>
              {inputValidationMessage.FinalComments}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Toast />
      <Grid style={LocalSiteButtons}>
        <Button
          title={t('Reject')}
          onClick={onRejectClick}
          disabled={
            disabled ||
            !disableGCButton ||
            Object.values(inputValidationMessage).some(msg => msg !== '')
          }
        />
        <Button
          title={t('Provide New Execution')}
          onClick={() => onProvideNewExecution(selectedRows)}
          disabled={disabled || selectedRows.length === 0}
        />
        <Button
          title={t('Add Department')}
          onClick={() => setShowAddDepartment(true)}
          disabled={disabled}
        />

        <Activity mode={showAddDepartment ? 'visible' : 'hidden'}>
          <AddDepartment>
            <AddDepartment.Header title={t('Add Department')} />
            <AddDepartment.Departments
              siteId={siteId}
              pecrId={pecrId}
              setSelectedDepartments={setSelectedDepartments}
              selectedDepartments={selectedDepartments}
              estimateData={estimateData}
            />
            <AddDepartment.ButtonRow>
              <CustomButton
                message="Cancel"
                type="cancel"
                size="medium"
                onClick={onCancel}
              />
              <CustomButton
                message="Save"
                type="primary"
                size="medium"
                onClick={onSave}
              />
            </AddDepartment.ButtonRow>
          </AddDepartment>
        </Activity>
        <Grid sx={{ position: 'relative' }}>
          <Button
            title={t('Save')}
            onClick={onSaveClick}
            disabled={disabled || !hasNotValidationError || showProgress}
          />
          {showProgress && (
            <CircularProgress size={28} sx={buttonCircularProgress} />
          )}
        </Grid>
        <Button
          title={t('PECR_complete')}
          onClick={onCompleteClick}
          disabled={disabled || !hasNotValidationError}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state = {}) => ({
  userInfo: state.userInfo,
  errorInAPI: state.errorNotification,
});

export default connect(mapStateToProps, null)(FinalReview);
